import { useState } from 'react';
import { MoreVertical, Edit, Copy, Trash2, CheckCircle, Archive } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import EditScenarioDialog from './EditScenarioDialog';
import DuplicateScenarioDialog from './DuplicateScenarioDialog';
import { scenariosApi } from '../../api/scenarios';

export default function ScenarioActionsMenu({ 
  scenario, 
  partNumber, 
  programId,
  onUpdate,
  disabled = false 
}) {
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showActivateDialog, setShowActivateDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleActivate = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await scenariosApi.activateScenario(partNumber, scenario.id, { programId });
      if (response.success) {
        setShowActivateDialog(false);
        onUpdate();
      } else {
        setError(response.error || 'Failed to activate scenario');
      }
    } catch (err) {
      setError(err.message || 'Failed to activate scenario');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await scenariosApi.deleteScenario(partNumber, scenario.id, { programId });
      if (response.success) {
        setShowDeleteDialog(false);
        onUpdate();
      } else {
        setError(response.error || 'Failed to delete scenario');
      }
    } catch (err) {
      setError(err.message || 'Failed to delete scenario');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            disabled={disabled}
            className="h-8 w-8 p-0"
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          <DropdownMenuItem onClick={() => setShowEditDialog(true)}>
            <Edit className="mr-2 h-4 w-4" />
            Edit Details
          </DropdownMenuItem>
          
          <DropdownMenuItem onClick={() => setShowDuplicateDialog(true)}>
            <Copy className="mr-2 h-4 w-4" />
            Duplicate
          </DropdownMenuItem>
          
          {!scenario.is_active && (
            <DropdownMenuItem onClick={() => setShowActivateDialog(true)}>
              <CheckCircle className="mr-2 h-4 w-4" />
              Activate Scenario
            </DropdownMenuItem>
          )}
          
          <DropdownMenuSeparator />
          
          <DropdownMenuItem 
            onClick={() => setShowDeleteDialog(true)}
            className="text-red-600 focus:text-red-600"
            disabled={scenario.is_active}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Edit Dialog */}
      <EditScenarioDialog
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        onSuccess={onUpdate}
        scenario={scenario}
        partNumber={partNumber}
        programId={programId}
      />

      {/* Duplicate Dialog */}
      <DuplicateScenarioDialog
        open={showDuplicateDialog}
        onClose={() => setShowDuplicateDialog(false)}
        onSuccess={onUpdate}
        scenario={scenario}
        partNumber={partNumber}
        programId={programId}
      />

      {/* Activate Confirmation */}
      <AlertDialog open={showActivateDialog} onOpenChange={setShowActivateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Activate Scenario?</AlertDialogTitle>
            <AlertDialogDescription>
              This will promote "{scenario.name}" to active status and archive the current active scenario.
              This action can be reversed later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {error && (
            <div className="text-sm text-red-600 p-3 bg-red-50 border border-red-200 rounded">
              {error}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleActivate} disabled={loading}>
              {loading ? 'Activating...' : 'Activate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Scenario?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{scenario.name}"? This will permanently delete all forecast data and cost components for this scenario.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {error && (
            <div className="text-sm text-red-600 p-3 bg-red-50 border border-red-200 rounded">
              {error}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              disabled={loading}
              className="bg-red-600 hover:bg-red-700"
            >
              {loading ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
